import { Service, inject } from '@angular/core'
import { ActivityGalleryResponse, EditImageBody, PremiseImagesResponse, ReorderGalleryBody } from '@seiri/shared/common'
import { ApiService } from '../api.service'

/**
 * The premise's logo and gallery, and each activity's gallery (docs/media.md). An upload is `multipart/form-data`
 * with one `file` field; `fetchJson` leaves a `FormData` body alone, so the browser sets the boundary.
 */
@Service()
export class ManageImageApiService {
  private readonly api = inject(ApiService)

  uploadLogo(entityId: string, file: Blob, init?: RequestInit) {
    return this.api.protectedFetchJson<PremiseImagesResponse>(`manage/entities/${entityId}/logo`, {
      method: 'PUT',
      body: fileBody(file),
      ...init,
    })
  }

  deleteLogo(entityId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<PremiseImagesResponse>(`manage/entities/${entityId}/logo`, {
      method: 'DELETE',
      ...init,
    })
  }

  uploadPremiseGalleryImage(entityId: string, file: Blob, init?: RequestInit) {
    return this.api.protectedFetchJson<PremiseImagesResponse>(`manage/entities/${entityId}/gallery`, {
      method: 'POST',
      body: fileBody(file),
      ...init,
    })
  }

  reorderPremiseGallery(entityId: string, body: ReorderGalleryBody, init?: RequestInit) {
    return this.api.protectedFetchJson<PremiseImagesResponse>(`manage/entities/${entityId}/gallery/order`, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...init,
    })
  }

  editPremiseGalleryImage(entityId: string, imageId: string, body: EditImageBody, init?: RequestInit) {
    return this.api.protectedFetchJson<PremiseImagesResponse>(`manage/entities/${entityId}/gallery/${imageId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
      ...init,
    })
  }

  deletePremiseGalleryImage(entityId: string, imageId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<PremiseImagesResponse>(`manage/entities/${entityId}/gallery/${imageId}`, {
      method: 'DELETE',
      ...init,
    })
  }

  uploadActivityGalleryImage(entityId: string, activityId: string, file: Blob, init?: RequestInit) {
    return this.api.protectedFetchJson<ActivityGalleryResponse>(
      `manage/entities/${entityId}/activities/${activityId}/gallery`,
      { method: 'POST', body: fileBody(file), ...init },
    )
  }

  reorderActivityGallery(entityId: string, activityId: string, body: ReorderGalleryBody, init?: RequestInit) {
    return this.api.protectedFetchJson<ActivityGalleryResponse>(
      `manage/entities/${entityId}/activities/${activityId}/gallery/order`,
      { method: 'PUT', body: JSON.stringify(body), ...init },
    )
  }

  editActivityGalleryImage(
    entityId: string,
    activityId: string,
    imageId: string,
    body: EditImageBody,
    init?: RequestInit,
  ) {
    return this.api.protectedFetchJson<ActivityGalleryResponse>(
      `manage/entities/${entityId}/activities/${activityId}/gallery/${imageId}`,
      { method: 'PATCH', body: JSON.stringify(body), ...init },
    )
  }

  deleteActivityGalleryImage(entityId: string, activityId: string, imageId: string, init?: RequestInit) {
    return this.api.protectedFetchJson<ActivityGalleryResponse>(
      `manage/entities/${entityId}/activities/${activityId}/gallery/${imageId}`,
      { method: 'DELETE', ...init },
    )
  }
}

function fileBody(file: Blob): FormData {
  const body = new FormData()
  body.append('file', file)
  return body
}
